/**
 * updatesService.ts — Fetches platform updates from Supabase with offline-first caching.
 *
 * Cache pattern (matches whisperSync.ts):
 *   1. readCache() returns fresh entries from localStorage if within TTL.
 *   2. On cache miss, fetches from Supabase, writes cache, returns mapped updates.
 *   3. Any error → returns stale cache if available, otherwise [].
 */
import { getClient } from '@retreats/supabase-adapter';
import { STORAGE_KEYS, TIMING } from '../constants';
import { createChildLogger } from '@retreat/shared';
import type { PlatformUpdate, UpdateCta, UpdateType, UpdateStatus } from '../types/updates';

const TABLE = 'platform_updates';
const CACHE_KEY = STORAGE_KEYS.UPDATES_CACHE;
const CACHE_TTL_MS = TIMING.DAY_MS;
const PUBLISHED: UpdateStatus = 'published';

const log = createChildLogger('updatesService');

// Raw DB row shape — mirrors the migration schema (snake_case)
export type UpdateRow = {
  id: string;
  published_at: string;
  type: UpdateType;
  title: string;
  body: string;
  cta_label: string | null;
  cta_action: string | null;
  status: UpdateStatus;
};

// Serialisable cache payload — publishedAt stored as ISO string
type CachePayload = {
  fetchedAt: number;
  updates: Array<Omit<PlatformUpdate, 'publishedAt'> & { publishedAt: string }>;
};

export function rowToUpdate(row: UpdateRow): PlatformUpdate {
  let cta: UpdateCta | null = null;
  if (row.cta_action !== null) {
    if (row.cta_action.startsWith('https://')) {
      cta = { kind: 'url', href: row.cta_action };
    } else {
      cta = { kind: 'route', target: row.cta_action as 'library' | 'settings' | 'home' };
    }
  }
  return {
    id: row.id,
    publishedAt: new Date(row.published_at),
    type: row.type,
    title: row.title,
    body: row.body,
    ctaLabel: row.cta_label,
    cta,
  };
}

export function readCacheSync(): PlatformUpdate[] {
  return readCache() ?? [];
}

function readCache(): PlatformUpdate[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const payload = JSON.parse(raw) as CachePayload;
    if (Date.now() - payload.fetchedAt > CACHE_TTL_MS) return null;
    if (payload.updates.length === 0) return null;
    return payload.updates.map((u) => ({ ...u, publishedAt: new Date(u.publishedAt) }));
  } catch {
    return null;
  }
}

function writeCache(updates: PlatformUpdate[]): void {
  try {
    const payload: CachePayload = {
      fetchedAt: Date.now(),
      updates: updates.map((u) => ({ ...u, publishedAt: u.publishedAt.toISOString() })),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // quota exceeded — pool still updated in-memory
  }
}

export async function fetchUpdates(): Promise<PlatformUpdate[]> {
  const cached = readCache();
  if (cached !== null) {
    log.debug(`updates loaded from cache (${cached.length} entries)`);
    return cached;
  }

  try {
    const client = getClient();
    const { data, error } = await client
      .from(TABLE)
      .select('id, published_at, type, title, body, cta_label, cta_action, status')
      .eq('status', PUBLISHED);

    if (error) {
      log.warn('updatesService: fetch failed', error.message);
      return [];
    }

    const updates = (data as UpdateRow[]).map(rowToUpdate);
    if (updates.length > 0) writeCache(updates);
    log.debug(`updates loaded from Supabase (${updates.length} entries)`);
    return updates;
  } catch (err) {
    log.warn('updatesService: unexpected error', err);
    return [];
  }
}

export async function fetchUpdatesLastReadAt(): Promise<Date | null> {
  try {
    const client = getClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (client.from('user_settings') as any)
      .select('updates_last_read_at')
      .maybeSingle() as { data: { updates_last_read_at: string | null } | null; error: unknown };
    if (error || !data?.updates_last_read_at) return null;
    return new Date(data.updates_last_read_at);
  } catch {
    return null;
  }
}

export async function writeUpdatesLastReadAt(userId: string, at: Date): Promise<void> {
  try {
    const client = getClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (client.from('user_settings') as any).upsert(
      { user_id: userId, updates_last_read_at: at.toISOString() },
      { onConflict: 'user_id' },
    );
  } catch {
    // fire-and-forget — optimistic update already applied in hook
  }
}
